/**
 * 共有 API クライアントコア（frontend/shared/auth）。
 *
 * createApi(auth, config) で認証モジュールとアプリ設定を注入し、get/post/put/del/recordLogin を返す。
 * 401 時は refreshAuthToken で強制リフレッシュして1回だけ再試行し、回復不能なら handleApiAuthError
 * （タイムアウトのポップアップ→ログイン）に回す。各アプリの lib/api.ts は戻り値を再エクスポートする。
 */

import type { AppAuthConfig } from './config';
import type { AuthModule } from './auth-core';

// APIのベースURL。環境変数指定が無ければ現在アクセスしているページのURLを使う（SSRは安全にフォールバック）。
const BASE_URL =
  process.env.NEXT_PUBLIC_API_URL ??
  (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000');

/** ログイン記録APIの呼び出し結果。allowed=false はこのアプリに許可されないユーザー種別。 */
export interface RecordLoginResult {
  allowed: boolean;
}

/**
 * API エラー。HTTPステータスとサーバ返却の code を保持する。
 * 呼び出し側が特定ステータス（例: 位置送信の409=稼働端末でない）を判別するために使う。
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export interface ApiModule {
  get: <T>(path: string, params?: Record<string, any>) => Promise<T>;
  post: <T>(path: string, data: Record<string, any>) => Promise<T>;
  put: <T>(path: string, data: Record<string, any>) => Promise<T>;
  del: <T>(path: string, data?: Record<string, any>) => Promise<T>;
  recordLogin: () => Promise<RecordLoginResult>;
}

export function createApi(auth: AuthModule, config: AppAuthConfig): ApiModule {
  const { apiPrefix, getCache } = config;

  // レスポンス処理。401（リフレッシュ後も回復不能）はセッションタイムアウト処理に回し、
  // 後続処理を止めるため解決しない Promise を返す（OK押下で画面遷移するため実害なし）。
  const handleResponse = async (response: Response) => {
    if (response.status === 401) {
      auth.handleApiAuthError();
      return new Promise(() => {});
    }
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      // status/code を保持した ApiError を投げる（呼び出し側が409等を判別できるようにする）。
      throw new ApiError(error.message || 'APIリクエストに失敗しました。', response.status, error.code);
    }
    return response.json();
  };

  const getHeaders = async () => {
    const token = await auth.getAuthToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  };

  // 401（トークン期限切れ等）の場合はリフレッシュトークンで強制リフレッシュして「1回だけ」再試行する。
  // doFetch は毎回 getHeaders() を呼び直すため、再試行時はリフレッシュ済みトークンが使われる。
  // リフレッシュできない（=回復不能）場合は元の401をそのまま返し、handleResponse 側でタイムアウト処理へ。
  const sendWithRetry = async (doFetch: () => Promise<Response>): Promise<Response> => {
    const response = await doFetch();
    if (response.status !== 401) return response;
    const refreshed = await auth.refreshAuthToken();
    if (!refreshed) return response;
    return await doFetch();
  };

  const get = async <T>(path: string, params?: Record<string, any>): Promise<T> => {
    const url = new URL(path, BASE_URL);
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          url.searchParams.append(key, String(params[key]));
        }
      });
    }
    const response = await sendWithRetry(async () =>
      fetch(url.toString(), {
        method: 'GET',
        headers: await getHeaders(),
        ...(getCache ? { cache: getCache } : {}),
      }),
    );
    return handleResponse(response);
  };

  const post = async <T>(path: string, data: Record<string, any>): Promise<T> => {
    const response = await sendWithRetry(async () =>
      fetch(new URL(path, BASE_URL).toString(), {
        method: 'POST',
        headers: await getHeaders(),
        credentials: 'include', // Cookieを含める（CSRF対策）
        body: JSON.stringify(data),
      }),
    );
    return handleResponse(response);
  };

  const put = async <T>(path: string, data: Record<string, any>): Promise<T> => {
    const response = await sendWithRetry(async () =>
      fetch(new URL(path, BASE_URL).toString(), {
        method: 'PUT',
        headers: await getHeaders(),
        credentials: 'include',
        body: JSON.stringify(data),
      }),
    );
    return handleResponse(response);
  };

  const del = async <T>(path: string, data?: Record<string, any>): Promise<T> => {
    const response = await sendWithRetry(async () =>
      fetch(new URL(path, BASE_URL).toString(), {
        method: 'DELETE',
        headers: await getHeaders(),
        credentials: 'include',
        body: JSON.stringify(data),
      }),
    );
    return handleResponse(response);
  };

  // ログイン記録API。ログイン成功後に呼ぶ。403 (USER_TYPE_NOT_ALLOWED) はこのアプリに入れない種別。
  const recordLogin = async (): Promise<RecordLoginResult> => {
    try {
      const response = await fetch(new URL(`${apiPrefix}/auth/login`, BASE_URL).toString(), {
        method: 'POST',
        headers: await getHeaders(),
        credentials: 'include',
        body: JSON.stringify({}),
      });
      if (response.status === 403) {
        const body = await response.json().catch(() => ({} as any));
        if (body?.code === 'USER_TYPE_NOT_ALLOWED') {
          return { allowed: false };
        }
      }
      return { allowed: true };
    } catch (error) {
      console.warn('ログイン記録APIの呼び出しに失敗しました:', error);
      return { allowed: true };
    }
  };

  return { get, post, put, del, recordLogin };
}
